import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { useGetSummary, useGetOfferings } from '../queries/onboardingPolls';
import {
  useUpdateProfileSummary,
  useUpdateOffering,
  useDeleteOffering,
} from '../mutations/onboarding';
import useNotification from './useNotification';

/**
 * Custom hook to manage summary and offerings data and mutations
 * Provides a unified interface for fetching and updating profile data
 * @param {boolean} enabled - Whether to enable data fetching (default: true)
 * @returns {Object} Summary and offerings data, loading states, and mutation handlers
 */
const useSummaryAndOfferings = (enabled = true) => {
  const { accessKey } = useAppStore();
  const queryClient = useQueryClient();
  const notification = useNotification();

  // Fetch profile summary
  const {
    data: profileSummary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useGetSummary(accessKey, enabled);

  // Fetch profile offerings
  const {
    data: profileOfferings,
    isLoading: offeringsLoading,
    error: offeringsError,
    refetch: refetchOfferings,
  } = useGetOfferings(accessKey, enabled);

  // Update profile summary mutation
  const updateSummaryMutation = useUpdateProfileSummary(
    (data) => {
      refetchSummary();
      notification.success('Profile summary updated successfully!');
    },
    (error) => {
      console.error('Error updating profile summary:', error);
      notification.error('Failed to update profile summary');
    }
  );

  // Update offering mutation
  const updateOfferingMutation = useUpdateOffering(
    (data) => {
      queryClient.invalidateQueries({ queryKey: ['offerings', accessKey] });
      queryClient.invalidateQueries({ queryKey: ['profile-offerings'] });
      notification.success('Offering updated successfully!');
    },
    (error) => {
      console.error('Error updating offering:', error);
      notification.error('Failed to update offering');
    }
  );

  // Delete offering mutation
  const deleteOfferingMutation = useDeleteOffering(
    (data) => {
      refetchOfferings();
      notification.success('Offering deleted successfully!');
    },
    (error) => {
      console.error('Error deleting offering:', error);
      notification.error('Failed to delete offering');
    }
  );

  // Handler functions
  const handleSaveSummary = (summaryData) => {
    updateSummaryMutation.mutate(summaryData);
  };

  const handleSaveOffering = (offeringId, offeringData) => {
    updateOfferingMutation.mutate({
      offeringId,
      offeringData,
    });
  };

  const handleDeleteOffering = (offeringId) => {
    deleteOfferingMutation.mutate({ offeringId });
  };

  return {
    // Summary data
    profileSummary,
    summaryLoading,
    summaryError,
    refetchSummary,

    // Offerings data
    profileOfferings,
    offeringsLoading,
    offeringsError,
    refetchOfferings,

    // Mutations
    updateSummaryMutation,
    updateOfferingMutation,
    deleteOfferingMutation,

    // Handlers
    handleSaveSummary,
    handleSaveOffering,
    handleDeleteOffering,

    // Loading states
    isSaving: updateSummaryMutation.isPending || updateOfferingMutation.isPending,
    isDeleting: deleteOfferingMutation.isPending,

    notification,
  };
};

export default useSummaryAndOfferings;
